import { HttpClient, PaginatedResponse, robotoHeaders } from "@/shared/http";

import {
  CreateDashboardRequest,
  DashboardRecord,
  UpdateDashboardRequest,
} from "./DashboardRecord";

export class DashboardService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createDashboard(
    request: CreateDashboardRequest,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<DashboardRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/dashboards/create`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
    return response.json<DashboardRecord>();
  }

  public async getDashboardById(
    dashboardId: string,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<DashboardRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/dashboards/id/${dashboardId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<DashboardRecord>();
  }

  public async getDashboardsForOrg(
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      pageToken: string;
    }>,
  ): Promise<PaginatedResponse<DashboardRecord>> {
    const searchParams = new URLSearchParams();

    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/dashboards`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<PaginatedResponse<DashboardRecord>>();
  }

  public async updateDashboard(
    dashboardId: string,
    request: UpdateDashboardRequest,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<DashboardRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/dashboards/id/${dashboardId}`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
    return response.json<DashboardRecord>();
  }

  public async deleteDashboard(
    dashboardId: string,
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/dashboards/id/${dashboardId}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    await response.throwIfError();
  }
}
